// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { ArrowClockwise, XCircle } from "@phosphor-icons/react";
import { Button, Flex } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2ReviewRejectScopePrompt,
  type ScopeChoice,
} from "./AskV2ReviewRejectScopePrompt";
import { type RejectionScope } from "./rememberedSentence";

type JudgmentTone = "reject" | "restore";

function JudgmentActionButton({
  "data-testid": dataTestId,
  label,
  icon: Icon,
  onClick,
  tone,
  disabled,
}: {
  "data-testid": string;
  label: string;
  icon: typeof XCircle | typeof ArrowClockwise;
  onClick: () => void;
  tone: JudgmentTone;
  disabled?: boolean;
}) {
  const isRestore = tone === "restore";

  return (
    <Button
      type="button"
      variant="outline"
      size="1"
      color="gray"
      data-testid={dataTestId}
      aria-label={label}
      aria-pressed={isRestore ? undefined : false}
      onClick={onClick}
      disabled={disabled}
      className="min-w-0 flex-1 basis-0"
    >
      <Flex as="span" gap="1" align="center" justify="center">
        <Icon size={14} weight="regular" aria-hidden="true" />
        {label}
      </Flex>
    </Button>
  );
}

export type AskV2ReviewCommentJudgmentActionsProps = {
  commentId: string;
  liked?: boolean | null;
  disabled?: boolean;
  onJudge: (liked: boolean | null, scope?: RejectionScope) => void;
  /** The finding's text, shown back as what Puntt will remember. */
  findingText?: string;
  /** The brand the finding names. When set, no scope question is asked. */
  brand?: string;
  /** Scope options offered when the finding names no brand. */
  scopeChoices?: ScopeChoice[];
};

export function AskV2ReviewCommentJudgmentActions({
  commentId,
  liked = null,
  disabled = false,
  onJudge,
  findingText = "",
  brand,
  scopeChoices,
}: AskV2ReviewCommentJudgmentActionsProps) {
  const { t } = useTranslation("ui");
  // A rejected finding stays in the list, grayed, so its card always offers
  // Restore; offering Delete too would let her re-reject a rejected row.
  const showRestore = liked === false;
  // why Reject opens a step instead of firing: rejecting is the click that
  // TEACHES, and the reviewer never sees what was learned unless it is shown
  // at the moment she decides.
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <AskV2ReviewRejectScopePrompt
        commentId={commentId}
        findingText={findingText}
        brand={brand}
        choices={scopeChoices}
        onConfirm={(scope) => {
          setConfirming(false);
          onJudge(false, scope);
        }}
        onCancel={() => setConfirming(false)}
      />
    );
  }

  return (
    <Flex
      gap="2"
      className="mt-3 border-t border-puntt-neutral-gray-5 pt-3"
      data-testid={`ask-v2-review-comment-judgment-${commentId}`}
    >
      {showRestore ? (
        <JudgmentActionButton
          data-testid={`ask-v2-review-comment-restore-${commentId}`}
          label={t("assetViewer.restore")}
          icon={ArrowClockwise}
          onClick={() => onJudge(null)}
          tone="restore"
          disabled={disabled}
        />
      ) : (
        <JudgmentActionButton
          data-testid={`ask-v2-review-comment-reject-${commentId}`}
          label={t("askV2.review.deleteFinding")}
          icon={XCircle}
          onClick={() => setConfirming(true)}
          tone="reject"
          disabled={disabled}
        />
      )}
    </Flex>
  );
}
